<template>
  <div class="field-validator">
    <slot></slot>
  </div>
</template>

<script>
import { TYPES as VALIDATION_TYPES } from '@/components/_shared/Form/Validation/FieldValidation.vue'

export const EVENT_TYPES = {
  demand: 'demand',
  blur: 'blur',
  change: 'change',
}

export const isEventType = value => {
  return Object.values(EVENT_TYPES).includes(value)
}

const DEFAULT_FAILED_VALIDATION = null

/**
 * Defines which validation types won't cause isValid to be set to false,
 * but only set isMessageVisible to true.
 */
const ALWAYS_VALID_VALIDATION_TYPES = [VALIDATION_TYPES.warning]

/**
 * Defines the order by type in which validations are run.
 * E.g. run error validations first.
 */
const VALIDATION_TYPE_VALIDATION_ORDER = [VALIDATION_TYPES.error, VALIDATION_TYPES.warning]

export default {
  name: 'FieldValidator',

  props: {
    validations: {
      type: Array,
      default: () => [],
      validator(value) {
        return value.every(({ validator }) => typeof validator === 'function')
      },
    },
    value: {
      required: true,
    },
    isRequired: {
      type: Boolean,
    },
    hasFocus: {
      type: Boolean,
      default: false,
    },
    /**
     * Defines for which event types isValid is updated.
     */
    validateOn: {
      type: Array,
      default: () => [EVENT_TYPES.change, EVENT_TYPES.blur],
      validator(value) {
        return value.every(isEventType)
      },
    },
    /**
     * Defines for which event and which validation types isMessageVisible is updated.
     */
    showMessageOn: {
      type: Object,
      default: () => ({ [EVENT_TYPES.blur]: [VALIDATION_TYPES.error, VALIDATION_TYPES.warning] }),
      validator(value) {
        return Object.keys(value).every(isEventType)
      },
    },
  },

  data() {
    return {
      isValid: false,
      isMessageVisible: false,
      failedValidation: DEFAULT_FAILED_VALIDATION,
    }
  },

  watch: {
    value() {
      this.updateOn(EVENT_TYPES.change)
    },

    hasFocus() {
      this.updateOn(EVENT_TYPES.blur)
    },

    isValid: {
      immediate: true,
      handler(isValid) {
        this.$emit('isValid', isValid)
      },
    },

    isRequired: {
      immediate: true,
      handler(isRequired) {
        if (!isRequired && !this.value) {
          this.isValid = true
        }
      },
    },

    isMessageVisible: {
      immediate: true,
      handler(isMessageVisible) {
        this.$emit('isMessageVisible', isMessageVisible)
      },
    },

    failedValidation: {
      immediate: true,
      handler(failedValidation) {
        this.$emit('failedValidation', failedValidation)
      },
    },
  },

  created() {
    this.sortValidations()
    if (this.value) {
      this.updateOn(EVENT_TYPES.change)
      this.updateOn(EVENT_TYPES.blur)
    }
  },

  methods: {
    updateOn(eventType = EVENT_TYPES.demand) {
      this.failedValidation = this.getFirstFailingValidation()
      if (eventType === EVENT_TYPES.blur && this.hasFocus) return
      if ([...this.validateOn, EVENT_TYPES.demand].includes(eventType)) {
        this.setIsValid()
      }
      if ([...Object.keys(this.showMessageOn), EVENT_TYPES.demand].includes(eventType)) {
        this.setIsMessageVisible(this.showMessageOn[eventType])
      }
    },

    forceShowMessage({ type, message }) {
      this.failedValidation = {
        type,
        message,
      }
      if (type === VALIDATION_TYPES.error) this.isValid = false
      this.isMessageVisible = true
    },

    setIsValid() {
      this.isValid =
        this.failedValidation === DEFAULT_FAILED_VALIDATION ||
        ALWAYS_VALID_VALIDATION_TYPES.includes(this.getValidationType(this.failedValidation))
    },

    setIsMessageVisible(updateForValidationTypes = []) {
      if (
        this.failedValidation === DEFAULT_FAILED_VALIDATION ||
        updateForValidationTypes.includes(this.getValidationType(this.failedValidation))
      ) {
        this.isMessageVisible = this.failedValidation !== DEFAULT_FAILED_VALIDATION
      }
    },

    /**
     * Marke sure validations are run ordered by type.
     */
    sortValidations() {
      this.validations.sort(
        ({ type: typeA }, { type: typeB }) =>
          VALIDATION_TYPE_VALIDATION_ORDER.indexOf(typeA) -
          VALIDATION_TYPE_VALIDATION_ORDER.indexOf(typeB)
      )
    },

    getFirstFailingValidation() {
      return (
        this.validations.find(({ validator }) => !validator(this.value)) ||
        DEFAULT_FAILED_VALIDATION
      )
    },

    getValidationType(validation) {
      return validation?.type || VALIDATION_TYPES.error
    },
  },
}
</script>
